import React, {FC, useCallback, useContext, useMemo, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Box, Button, Typography, useTheme} from "@material-ui/core";
import {useDispatch, useSelector} from "react-redux";
import {CardElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {
  StripeCardElement,
  StripeCardElementChangeEvent,
} from "@stripe/stripe-js";
import clsx from "clsx";
import Alert from "view/components/Alert";
import {
  goToPayment,
  resetCartStepper,
  selectTotalAmount,
} from "store/exhibitors/cartStepper/slice";
import stripeService from "api/stripe";
import formatPrice from "tools/formats/formatPrice";
import {CurrencyContext} from "view/context/CurrencyContext";
import {useBeforeUnload} from "hooks";
import orderService from "api/order";
import BackdropLoader from "view/components/BackdropLoader";

import FlexBox from "view/components/Layout/FlexBox";
import {ErrorType} from "ts/api/error";
import {CardPayment, PaymentMethodType} from "ts/api/paymentMethod";
import getTransfers from "tools/order/getTransfers";
import useRedirectToOrderPage from "view/apps/Exhibitor/pages/Cart/components/Payment/components/useRedirectToOrderPage";
import {CartWithApprovalAmount} from "store/exhibitors/cartStepper/helper";
import {CardOrder, OrderProduct, OrderStatus} from "ts/api/order";
import {apiV2, Timestamp} from "api/firebase";

import orderRefs from "api/shows/orders/refs";
import OrdersList, {
  StepOrder,
} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList";
import {KeyboardBackspaceSharp} from "@material-ui/icons";
import {setShowCartCount} from "store/badges/slice";
import {removeOrderProductsFromCart} from "tools/order/removeOrderProductsFromCart";
import {getCartCount} from "tools/product/getCartCount";
import {getOrdersCount} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/helpers/getOrdersCount";
import {
  IExhibitorPair,
  IOrganiser,
  IShow,
  Theme,
} from "@evennt-tech/api-v2-core";
import useBranding from "store/branding/branding.store";
import {generateTestId} from "tools/cypress/generateTestId";
import {
  useIsShowDiscountCodesInvalid,
  useShowDiscountCodesActions,
} from "store/discount-code/discount-code-store";
import {useQuickOrderId} from "hooks/show/order";
import {getUniqueProductCategoryIdsBySupplier} from "tools/product/getUniqueProductCategoryIdsBySupplier";
import {useMultiStepOrders} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/helpers/useMultiStepOrders";
import {getTruncatedString} from "tools/common/getTruncatedString";
import {format} from "date-fns";
import {getReadableString} from "constraints/general";
import {useStyles} from "../../../style";
import {getCardElementOptions} from "./helpers/getCardElementOptions";
import getCountryByCurrency from "../../../../helpers/getCountryByCurrency";
import {CartFlow, CartFlowType} from "../../types";
import incrementProductsPurchasedQuantity from "./helpers/incrementProductsPurchasedQuantity";
import {PlaceOrderCallback} from "../../../types";
import {resetQuickOrderPaymentAttempt} from "./helpers/resetQuickOrderPaymentAttempt";

type Props = CartFlow & {
  show: IShow;
  exhibitorPair: IExhibitorPair;
  orderId?: string;
  organiser: IOrganiser;
  orderDescription: string;
  transferredPaymentDescription: string;
  onPlaceOrder: PlaceOrderCallback;
};

const getTotalAmount = (amounts: CartWithApprovalAmount) => {
  if (!amounts) {
    return 0;
  }
  return Object.entries(amounts).reduce<number>(
    (acc, [_, total]) => acc + total,
    0
  );
};

const Card: FC<Props> = ({
  type: cartFlowType,
  products,
  show,
  exhibitorPair,
  organiser,
  orderDescription: description,
  transferredPaymentDescription,
  onPlaceOrder,
  orderId,
}) => {
  const quickOrderId = useQuickOrderId();
  const {palette} = useTheme();
  const {getBranding} = useBranding();
  const isDark = getBranding(show.id)?.theme === Theme.DARK;
  const classes = useStyles();
  const {t} = useTranslate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const currency = useContext(CurrencyContext);
  const totalAmount = useSelector(selectTotalAmount);
  const {
    reset: resetAppliedDiscountCodes,
    validate: validateDiscountCodes,
  } = useShowDiscountCodesActions();
  const isDiscountCodesInvalid = useIsShowDiscountCodesInvalid(show.id);

  const [isMultiStepFlow, setIsMultiStepFlow] = useState<boolean>(false);
  const [isStepperSubmitting, setIsStepperSubmitting] = useState(false);
  const [stepperState, setStepperState] = useState<StepOrder[]>([]);
  const [multiStepOrders, setMultiStepOrders] = useMultiStepOrders({
    show,
    stepperState,
    products,
    organiser,
    transferredPaymentDescription,
    exhibitorPairId: exhibitorPair.id,
  });
  const [orderGroupId, setOrderGroupId] = useState<string>("");

  const ordersCount = useMemo(() => getOrdersCount(products), [products]);
  const categoryIdsBySupplier = useMemo(
    () => getUniqueProductCategoryIdsBySupplier(products),
    [products]
  );

  const redirectToOrderPage = useRedirectToOrderPage(show.id, exhibitorPair.id);

  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCardButtonReady, setIsCardButtonReady] = useState(false);

  const shouldWarnBeforeUnload =
    isSubmitting || (isMultiStepFlow && stepperState.some((o) => !o.isPaid));

  useBeforeUnload(shouldWarnBeforeUnload);

  const redirectToSuccess = useCallback(() => {
    if (orderGroupId) {
      redirectToOrderPage(orderGroupId, orderGroupId);
    }
  }, [orderGroupId, redirectToOrderPage]);

  const getCardElement = () => {
    if (!stripe || !elements) {
      throw new Error("Stripe or its element is not ready");
    }
    const card = elements.getElement(CardElement);
    if (!card) {
      throw new Error("Card element don't ready");
    }
    return card;
  };

  const updateCartInfo = (orderProducts: OrderProduct[]) => {
    removeOrderProductsFromCart(orderProducts);
    const cartCount = getCartCount();
    dispatch(setShowCartCount(cartCount));
  };

  const handleSingleOrderPayment = async (
    order: CardOrder,
    card: StripeCardElement
  ) => {
    let finalPaymentIntentId = "";

    try {
      if (!stripe) {
        throw new Error("Stripe is not ready");
      }

      const orderId = quickOrderId || order.id;

      await orderRefs
        .getCollection({showId: show.id})
        .doc(orderId)
        .set({...order, createdAt: Timestamp.now()});
      const isApprovalRequired = !!order.review;

      const {
        clientSecret,
        paymentIntentId,
      } = await stripeService.makePayment<CardPayment>({
        amount: order.paymentDetails.amount,
        description,
        currency,
        paymentType: !isApprovalRequired ? "automatic" : "manual",
        orderId,
        country: getCountryByCurrency(currency),
        showId: show.id,
        type: PaymentMethodType.cardPayment,
        showName: getTruncatedString(show.name, 50),
        showStartDate: format(
          new Date(show.liveStart),
          `${getReadableString(show)}, HH:mm`
        ),
      });

      finalPaymentIntentId = paymentIntentId;
      const updatedOrder: Partial<CardOrder> = {
        paymentIntentId,
        useNewProcess: true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        "paymentDetails.status": OrderStatus.processing,
      };
      await orderRefs
        .getCollection({showId: show.id})
        .doc(orderId)
        .update(updatedOrder);

      const {error, paymentIntent} = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card,
          },
        }
      );

      const isPaymentIntentSuccessful =
        paymentIntent?.status ===
        (isApprovalRequired ? "requires_capture" : "succeeded");

      if (error?.message || !isPaymentIntentSuccessful) {
        console.warn("Payment method didn't created");
        throw new Error(error?.message);
      }
      await orderRefs.getDocument({showId: show.id, orderId}).update({
        "paymentDetails.status": "paid",
        "paymentDetails.paymentDate": Timestamp.now(),
      });

      await orderService.markOrderProductImagesUsed(order.products);
      updateCartInfo(order.products);
    } catch (e) {
      if (finalPaymentIntentId) {
        await apiV2.oldStripe
          .cancelPayment({
            paymentIntent: finalPaymentIntentId,
            currency,
            useNewProcess: true,
          })
          .catch((e) => {
            console.error(
              `Error occurred canceling payment: ${finalPaymentIntentId}`,
              e
            );
          });
      }
      if (quickOrderId) {
        await resetQuickOrderPaymentAttempt({
          showId: show.id,
          orderId: quickOrderId,
        }).catch((error) => {
          console.error(
            "Error occurred resetting quick order payment: ",
            error
          );
        });
        throw e;
      }
      await orderRefs
        .getDocument({showId: show.id, orderId: order.id})
        .delete()
        .catch((e) => {
          console.error("Error occurred deleting order: ", e);
        });
      throw e;
    }
  };

  const handleMakeObsoletePayment = async (
    orderId: string,
    card: StripeCardElement,
    // TODO: Remove this argurement and related functionality when all the old orders are paid / canceled.
    // Also Remove `handleNewFlowApprovalPayment` and move its functionality here
    isRequiredApproval?: boolean,
    useAutho?: boolean
  ) => {
    if (!stripe) {
      return null;
    }
    const {clientSecret} = await stripeService.makePayment<CardPayment>({
      amount: totalAmount.withoutApproval,
      description,
      currency,
      paymentType: useAutho ? "automatic" : "manual",
      orderId,
      country: getCountryByCurrency(currency),
      showId: show.id,
      type: PaymentMethodType.cardPayment,
      showName: getTruncatedString(show.name, 50),
      showStartDate: format(
        new Date(show.liveStart),
        `${getReadableString(show)}, HH:mm`
      ),
    });

    const {error, paymentIntent} = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card,
        },
      }
    );

    if (error?.message || !paymentIntent) {
      if (isRequiredApproval) {
        await orderService.moveToAwaitingForPayment(show.id, orderId);
      } else {
        await orderService.deleteItem(show.id, orderId);
      }
      console.warn("Payment method didn't created");
      throw new Error(error?.message);
    }

    return paymentIntent.id;
  };
  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || !organiser || isMultiStepFlow) {
      return null;
    }
    const isDiscountCodesValid = await validateDiscountCodes(
      show.id,
      exhibitorPair.standId,
      categoryIdsBySupplier
    );
    if (!isDiscountCodesValid) {
      return null;
    }
    if (error) {
      setError("");
    }

    try {
      setIsSubmitting(true);

      if (cartFlowType === CartFlowType.local) {
        const card = getCardElement();

        if (ordersCount === 1) {
          const placeOrderResult = await onPlaceOrder({isDraft: true});
          if (!placeOrderResult) {
            throw new Error("Order(s) didn't create");
          }
          const {orderWithoutApproval, ordersWithApproval} = placeOrderResult;

          const order = (orderWithoutApproval ??
            ordersWithApproval?.[0]) as CardOrder;
          if (!order) {
            throw new Error("Order didn't create");
          }

          await handleSingleOrderPayment(order, card);
          dispatch(resetCartStepper());
          resetAppliedDiscountCodes(show.id);
          redirectToOrderPage(order.id);
        } else {
          await onMultiPayment();
        }
      } else if (cartFlowType === CartFlowType.created && orderId) {
        // NOTE: This is obsolete flow. We will need to remove it after all the orders are paid / canceled
        const transfers = await getTransfers({
          products,
          organiser,
          show,
          transferredPaymentDescription,
          orderId,
        });

        await orderService.proceedToPayment(show.id, orderId, {
          paymentType: PaymentMethodType.cardPayment,
          transfers,
        });
        const card = getCardElement();
        await handleMakeObsoletePayment(orderId, card, true, true);
        await incrementProductsPurchasedQuantity(show.id, products);
        await orderService.markOrderProductImagesUsed(products);
        redirectToOrderPage(orderId);
      }
    } catch (error) {
      const typedError = error as ErrorType;
      console.error("error", typedError.message);
      if (typedError.message) {
        setError(typedError.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    cartFlowType,
    onPlaceOrder,
    orderId,
    stripe,
    elements,
    ordersCount,
    isMultiStepFlow,
  ]);

  const onMultiPayment = async () => {
    try {
      if (!stripe) {
        throw new Error("Stripe is not ready");
      }
      setIsMultiStepFlow(true);

      const placeOrderResult = await onPlaceOrder({isDraft: true});
      if (!placeOrderResult) {
        throw new Error("Orders weren't created");
      }

      const {
        orderWithoutApproval,
        ordersWithApproval = [],
        groupId,
      } = placeOrderResult;
      const ordersCount =
        (orderWithoutApproval ? 1 : 0) + ordersWithApproval?.length || 0;
      if (ordersCount === 1) {
        throw new Error("Multi-step payment expects multiple orders");
      }

      setOrderGroupId(groupId ?? "");

      const allNewOrders = [
        ...(ordersWithApproval as CardOrder[]),
        ...(orderWithoutApproval ? [orderWithoutApproval as CardOrder] : []),
      ];
      const newStepperState = allNewOrders.map<StepOrder>((order) => ({
        id: order.id,
        isPaid: false,
        attempts: 0,
      }));

      setStepperState(newStepperState);
      setMultiStepOrders(allNewOrders);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
        console.error("Error occurred attempting payment: ", e);
      }
    }
  };

  const isSplitOrders = ordersCount > 1;
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    return await handleSubmit();
  };

  const shouldRenderStepper = !!(stepperState.length && isMultiStepFlow);

  const handleStepOrder = async (order: CardOrder) => {
    try {
      const card = getCardElement();
      await handleSingleOrderPayment(order, card);

      setStepperState((prev) =>
        prev.map((val) =>
          val.id === order.id ? {...val, isPaid: true, error: ""} : val
        )
      );
    } catch (e: any) {
      setStepperState((prev) =>
        prev.map((stepOrder) =>
          stepOrder.id === order.id
            ? {
                ...stepOrder,
                isPaid: false,
                error: e?.message || "Unknown error",
                attempts: stepOrder.attempts + 1,
              }
            : stepOrder
        )
      );
      throw e;
    }
  };
  const buttonText = (() => {
    if (!isSplitOrders) {
      return t("exhibitor.cart.payment.pay_amount", {
        amount: formatPrice(
          totalAmount.withoutApproval +
            getTotalAmount(totalAmount.withApproval),
          currency
        ),
      });
    }

    return (
      <FlexBox alignCenter gap={1}>
        <Typography variant="button">
          <T keyName="exhibitor.cart.payment.checkout" />
        </Typography>
        <KeyboardBackspaceSharp
          style={{
            transform: "rotate(180deg)",
          }}
        />
      </FlexBox>
    );
  })();
  const isPaymentDisabled =
    !isComplete || isSubmitting || !isCardButtonReady || isDiscountCodesInvalid;

  return (
    <>
      <Box mt={3}>
        <BackdropLoader isLoading={isSubmitting && !isMultiStepFlow} />
        <form onSubmit={onSubmit}>
          <CardElement
            options={getCardElementOptions({
              palette,
              isDark,
              disabled: isSubmitting || isStepperSubmitting,
            })}
            onReady={() => {
              setIsCardButtonReady(true);
            }}
            onChange={(event: StripeCardElementChangeEvent) => {
              if (error) {
                setError("");
              }

              if (event.error?.message) {
                setError(event.error?.message);
              }

              if (isComplete !== event.complete) {
                setIsComplete(event.complete);
              }
            }}
            className={clsx(
              classes.stripeInput,
              !!error && classes.stripeInputError,
              !isCardButtonReady && classes.stripeInputDisabled
            )}
          />
          {!!error && (
            <Alert severity="error" className={classes.alert}>
              {error}
            </Alert>
          )}
          {!isMultiStepFlow && (
            <FlexBox
              mt={3}
              justifyContent={
                cartFlowType === CartFlowType.local
                  ? "space-between"
                  : "flex-end"
              }
            >
              {cartFlowType === CartFlowType.local && (
                <Button
                  {...generateTestId(
                    "exhibitor-cart-change-billing-details-btn"
                  )}
                  className={classes.buttonBackToCart}
                  color="primary"
                  onClick={() => dispatch(goToPayment())}
                >
                  <T keyName="exhibitor.cart.payment.change_billing_details" />
                </Button>
              )}
              <Button
                {...generateTestId("exhibitor-cart-pay-btn")}
                type="submit"
                disabled={isPaymentDisabled}
                classes={{text: classes.buttonText}}
                color="primary"
                variant="contained"
                onClick={() => null}
              >
                {buttonText}
              </Button>
            </FlexBox>
          )}
        </form>
        {shouldRenderStepper && (
          <FlexBox column>
            <OrdersList
              orders={multiStepOrders}
              stepperState={stepperState}
              handleSingleOrderPayment={handleStepOrder}
              redirectToSuccess={redirectToSuccess}
              isPaymentDisabled={isPaymentDisabled}
              isStepperSubmitting={isStepperSubmitting}
              setIsStepperSubmitting={setIsStepperSubmitting}
              categoryIdsBySupplier={categoryIdsBySupplier}
              newStandId={exhibitorPair.standId}
            />
          </FlexBox>
        )}
      </Box>
    </>
  );
};

export default Card;
